// src/pages/AdminDashboard.jsx
import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import toast, { Toaster } from 'react-hot-toast';
import { validateAdminPassword, decrementStockById, incrementStockById } from '../utils/adminUtils';
import { getFigurineImageSrc } from '../utils/figurineMedia';
import { SectionHeading } from '../components/BrandUI';
import { uploadFigureImagesToCloudinary } from '../utils/cloudinaryUpload';

export default function AdminDashboard() {
  const [password, setPassword] = useState('');
  const [isAdmin, setIsAdmin] = useState(sessionStorage.getItem('isAdmin') === 'true');
  const [figurines, setFigurines] = useState([]);
  const [search, setSearch] = useState('');
  const [inStockOnly, setInStockOnly] = useState(true);
  const [editingFigure, setEditingFigure] = useState(null);
  const [editName, setEditName] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editRating, setEditRating] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [importFiles, setImportFiles] = useState([]);
  const [detectedFigures, setDetectedFigures] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importPreviews, setImportPreviews] = useState([]);

  const updateImportFiles = useCallback((files, append = false) => {
    const imageFiles = Array.from(files).filter((file) => file?.type.startsWith('image/'));
    if (imageFiles.length === 0) return;

    setImportFiles((currentFiles) => append ? [...currentFiles, ...imageFiles] : imageFiles);
    setDetectedFigures([]);
  }, []);

  const clearImportImages = () => {
    setImportFiles([]);
    setDetectedFigures([]);
  };

  const handleLogin = (password) => {
    if (validateAdminPassword(password)) {
      setIsAdmin(true);
      sessionStorage.setItem('isAdmin', 'true');
      fetchFigures();
    } else {
      toast.error("Incorrect password");
    }
  };

  const fetchFigures = async () => {
    const { data, error } = await supabase.from('figurines').select('*').order('name', { ascending: true });
    if (error) {
      console.error(error);
      toast.error("Failed to load figures");
    } else {
      setFigurines(data);
    }
  };

  const handleDecrement = async (id) => {
    const updated = await decrementStockById(id);
    if (updated) fetchFigures();
  };

  const handleIncrement = async (id) => {
    const updated = await incrementStockById(id);
    if (updated) fetchFigures();
  };

  const openEditModal = (fig) => {
    setEditingFigure(fig);
    setEditName(fig.name);
    setEditPrice(String(fig.value));
    setEditRating(String(fig.rating));
  };

  const closeEditModal = () => {
    setEditingFigure(null);
    setEditName('');
    setEditPrice('');
    setEditRating('');
  };

  const handleSaveEdit = async () => {
    const trimmedName = editName.trim();
    const price = Number(editPrice);
    const rating = Number(editRating);

    if (!trimmedName) {
      toast.error('Please enter a name');
      return;
    }

    if (editPrice.trim() === '' || !Number.isFinite(price) || price <= 0) {
      toast.error('Please enter a valid price');
      return;
    }

    if (
      editRating.trim() === '' ||
      !Number.isInteger(rating) ||
      rating < 3 ||
      rating > 7
    ) {
      toast.error('Please enter a rating from 3 to 7');
      return;
    }

    if (!editingFigure || isSaving) return;

    setIsSaving(true);

    try {
      const { error } = await supabase
        .from('figurines')
        .update({
          name: trimmedName,
          value: price,
          rating,
        })
        .eq('id', editingFigure.id);

      if (error) {
        console.error(error);
        toast.error('Failed to update figure');
        return;
      }

      toast.success('Figure updated successfully');
      closeEditModal();
      await fetchFigures();
    } catch (error) {
      console.error(error);
      toast.error('Failed to update figure');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteFigure = async () => {
    if (!editingFigure || isDeleting) return;

    if (editingFigure.stock !== 0) {
      toast.error('Only figures with zero stock can be deleted.');
      return;
    }

    if (!window.confirm(`Delete ${editingFigure.name}? This cannot be undone.`)) return;

    setIsDeleting(true);

    try {
      const { error } = await supabase.from('figurines').delete().eq('id', editingFigure.id);

      if (error) {
        console.error(error);
        toast.error('Failed to delete figure.');
        return;
      }

      toast.success('Figure deleted successfully.');
      closeEditModal();
      await fetchFigures();
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete figure.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAnalyzeImport = async () => {
    if (importFiles.length === 0) {
      toast.error('Choose at least one figure image.');
      return;
    }

    setIsAnalyzing(true);

    try {
      const imageUrls = await uploadFigureImagesToCloudinary(importFiles);
      const response = await fetch('/api/analyze-figures', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageUrls }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || `Figure analysis failed (HTTP ${response.status}).`);
      }

      setDetectedFigures(data.figures.map((figure, index) => ({
        ...figure,
        value: figure.value ?? '',
        rating: figure.rating ?? '',
        stock: figure.stock ?? 1,
        key: `${Date.now()}-${index}`,
      })));
      toast.success('Figures are ready for review.');
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Unable to analyze these images.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const updateDetectedFigure = (key, field, value) => {
    setDetectedFigures((figures) => figures.map((figure) => (
      figure.key === key ? { ...figure, [field]: value } : figure
    )));
  };

  const handleConfirmImport = async () => {
    if (detectedFigures.length === 0 || isImporting) return;

    const rows = detectedFigures.map((figure) => ({
      name: figure.name.trim(),
      series: figure.series.trim(),
      value: Number(figure.value),
      rating: Number(figure.rating),
      stock: Number(figure.stock),
      image: figure.image,
    }));

    const invalidRow = rows.some((figure) => (
      !figure.name ||
      !figure.series ||
      !Number.isFinite(figure.value) || figure.value <= 0 ||
      !Number.isInteger(figure.rating) || figure.rating < 3 || figure.rating > 7 ||
      !Number.isInteger(figure.stock) || figure.stock < 0
    ));

    if (invalidRow) {
      toast.error('Each figure needs a name, series, positive price, rating 3–7, and stock of 0 or more.');
      return;
    }

    setIsImporting(true);

    try {
      const { error } = await supabase.from('figurines').insert(rows);

      if (error) {
        console.error(error);
        toast.error('Failed to import figures.');
        return;
      }

      setDetectedFigures([]);
      setImportFiles([]);
      await fetchFigures();
      toast.success(`${rows.length} figure${rows.length === 1 ? '' : 's'} imported successfully.`);
    } catch (error) {
      console.error(error);
      toast.error('Failed to import figures.');
    } finally {
      setIsImporting(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchFigures();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return undefined;

    const handlePaste = (event) => {
      const imageFiles = Array.from(event.clipboardData?.items || [])
        .filter((item) => item.type.startsWith('image/'))
        .map((item) => item.getAsFile())
        .filter(Boolean);

      if (imageFiles.length === 0) return;

      event.preventDefault();
      updateImportFiles(imageFiles, true);
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [isAdmin, updateImportFiles]);

  useEffect(() => {
    const previews = importFiles.map((file) => URL.createObjectURL(file));
    setImportPreviews(previews);

    return () => previews.forEach((preview) => URL.revokeObjectURL(preview));
  }, [importFiles]);

  const filteredFigures = figurines.filter((fig) => {
    const matchesSearch =
      fig.name.toLowerCase().includes(search.toLowerCase()) ||
      fig.series.toLowerCase().includes(search.toLowerCase());

    const matchesStock = !inStockOnly || fig.stock > 0;

    return matchesSearch && matchesStock;
  });

  if (!isAdmin) {
    return (
      <div className="nfig-page flex items-center justify-center">
        <Toaster position="top-center" />
        <div className="nfig-panel relative z-10 w-full max-w-md rounded-2xl p-7 sm:p-9">
          <p className="nfig-kicker mb-3">Restricted access</p>
          <h2 className="text-3xl font-black uppercase text-white">Admin Login</h2>
          <p className="mt-2 text-sm text-slate-400">Enter the dashboard password to manage the collection.</p>
          <input
            type="password"
            placeholder="Enter admin password"
            className="nfig-input mt-6"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(password); }}
          />
          <button onClick={() => handleLogin(password)} className="nfig-button-primary mt-4 w-full">Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="nfig-page">
      <Toaster position="top-center" />
      <div className="nfig-container">
        <div className="mb-9">
          <p className="nfig-kicker mb-3">Collection control</p>
          <h1 className="nfig-page-title">Admin Dashboard</h1>
          <p className="mt-3 text-slate-400">Manage figure details and stock levels.</p>
        </div>

        <section className="nfig-panel mb-9 rounded-xl p-5">
          <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center">
            <input
              type="text"
              placeholder="Search figures..."
              className="nfig-input w-full max-w-lg"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              onClick={() => setInStockOnly((prev) => !prev)}
              className={inStockOnly ? 'nfig-button-primary whitespace-nowrap' : 'nfig-button-secondary whitespace-nowrap'}
            >
              {inStockOnly ? 'Show all figures' : 'Show in-stock only'}
            </button>
          </div>
        </section>

        <section className="nfig-panel mb-9 rounded-xl p-5 sm:p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="nfig-kicker mb-2">Cloudinary + AI</p>
              <h2 className="text-2xl font-black uppercase text-white">AI Import Figures</h2>
              <p className="mt-2 max-w-2xl text-sm text-slate-400">Upload one or more photos. A photo can contain multiple figures; review every detected row before it is added.</p>
            </div>
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap lg:w-auto lg:justify-end">
              <label className="nfig-button-secondary cursor-pointer whitespace-nowrap">
                Choose images
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="sr-only"
                  onChange={(event) => {
                    updateImportFiles(event.target.files || []);
                    event.target.value = '';
                  }}
                />
              </label>
              <button
                type="button"
                onClick={handleAnalyzeImport}
                disabled={isAnalyzing || importFiles.length === 0}
                className="nfig-button-primary whitespace-nowrap"
              >
                {isAnalyzing ? 'Analyzing...' : 'Upload & Analyze'}
              </button>
              <button
                type="button"
                onClick={clearImportImages}
                disabled={importFiles.length === 0 || isAnalyzing || isImporting}
                className="nfig-button-secondary whitespace-nowrap"
              >
                Clear Images
              </button>
            </div>
          </div>
          <p className="mt-4 text-sm text-slate-400">Paste image here (Ctrl+V), or choose one or more files.</p>
          {importFiles.length > 0 && (
            <div className="mt-4">
              <p className="text-sm text-blue-300" aria-live="polite">{importFiles.length} image{importFiles.length === 1 ? '' : 's'} selected.</p>
              <div className="mt-3 flex flex-wrap gap-3">
                {importPreviews.map((preview, index) => (
                  <div key={preview} className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-lg border border-blue-400/20 bg-slate-100 p-1">
                    <img
                      src={preview}
                      alt={importFiles[index]?.name || `Selected image ${index + 1}`}
                      className="h-full w-full object-contain"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {detectedFigures.length > 0 && (
            <div className="mt-6 border-t border-white/10 pt-6">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="nfig-kicker">Review before import</p>
                  <p className="mt-1 text-sm text-slate-400">Edit uncertain values or remove incorrect detections.</p>
                </div>
                <button type="button" onClick={handleConfirmImport} disabled={isImporting} className="nfig-button-primary">
                  {isImporting ? 'Importing...' : `Confirm ${detectedFigures.length} Figure${detectedFigures.length === 1 ? '' : 's'}`}
                </button>
              </div>

              <div className="space-y-4">
                {detectedFigures.map((figure) => (
                  <article key={figure.key} className="grid gap-4 rounded-xl border border-blue-400/20 bg-black/20 p-4 md:grid-cols-[120px_1fr_auto] md:items-start">
                    <div className="flex h-28 items-center justify-center rounded-lg bg-slate-100 p-2">
                      <img src={figure.image} alt="Uploaded figure" className="h-full w-full object-contain" />
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                      {[
                        ['name', 'Name', 'text'],
                        ['series', 'Series', 'text'],
                        ['value', 'Price', 'number'],
                        ['rating', 'Rating', 'number'],
                        ['stock', 'Stock', 'number'],
                      ].map(([field, label, type]) => (
                        <label key={field}>
                          <span className="mb-1 block text-[0.65rem] font-bold uppercase tracking-widest text-slate-400">{label}</span>
                          <input
                            type={type}
                            min={field === 'value' ? '0.01' : field === 'rating' ? '3' : field === 'stock' ? '0' : undefined}
                            max={field === 'rating' ? '7' : undefined}
                            step={type === 'number' ? 'any' : undefined}
                            value={figure[field]}
                            onChange={(event) => updateDetectedFigure(figure.key, field, event.target.value)}
                            className="nfig-input px-3 py-2 text-sm"
                          />
                        </label>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => setDetectedFigures((figures) => figures.filter((item) => item.key !== figure.key))}
                      className="nfig-button-danger px-3 py-2 text-xs"
                    >
                      Remove
                    </button>
                  </article>
                ))}
              </div>
            </div>
          )}
        </section>

        <SectionHeading eyebrow={`${filteredFigures.length} figures`} title="Inventory" />

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-6 lg:grid-cols-4">
          {filteredFigures.map((fig) => (
            <article key={fig.id} className="nfig-panel overflow-hidden rounded-xl text-center transition hover:border-blue-400/50">
              <div className="flex aspect-square items-center justify-center bg-gradient-to-b from-slate-100 to-blue-50 p-3">
                <img src={getFigurineImageSrc(fig)} alt={fig.name} className="h-full w-full object-contain transition duration-300 hover:scale-105" />
              </div>
              <div className="p-4">
                <p className="truncate text-[0.65rem] font-bold uppercase tracking-widest text-blue-300">{fig.series}</p>
                <h2 className="mt-1 line-clamp-2 min-h-[3rem] text-lg font-bold leading-snug">{fig.name}</h2>
                <div className="mt-3 flex items-center justify-between border-y border-white/10 py-2 text-sm">
                  <span className="font-bold text-[#f5c451]">★ {fig.rating}</span>
                  <span className="font-black">₪{fig.value}</span>
                </div>
                <p className={`mt-3 text-sm font-black uppercase tracking-wider ${fig.stock > 0 ? 'text-emerald-300' : 'text-red-300'}`}>Stock: {fig.stock}</p>
                <div className="mt-3 flex justify-center gap-2">
                  <button onClick={() => handleDecrement(fig.id)} className="flex h-10 flex-1 items-center justify-center rounded-lg border border-red-400/30 bg-red-500/10 text-lg text-red-200 transition hover:bg-red-500/20" aria-label={`Decrease ${fig.name} stock`}>−</button>
                  <button onClick={() => handleIncrement(fig.id)} className="flex h-10 flex-1 items-center justify-center rounded-lg border border-emerald-400/30 bg-emerald-500/10 text-lg text-emerald-200 transition hover:bg-emerald-500/20" aria-label={`Increase ${fig.name} stock`}>+</button>
                </div>
                <button onClick={() => openEditModal(fig)} className="nfig-button-secondary mt-3 w-full">Edit</button>
              </div>
            </article>
          ))}
        </div>
      </div>

      {editingFigure && (
        <div className="nfig-modal-backdrop" onClick={() => closeEditModal()}>
          <div className="nfig-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              aria-label="Close edit modal"
              onClick={() => closeEditModal()}
              className="nfig-close"
            >
              ×
            </button>

            <div className="flex flex-col items-center text-center mb-5">
              <div className="mb-4 flex h-40 w-full items-center justify-center rounded-xl bg-gradient-to-b from-slate-100 to-blue-50 p-3">
                <img src={getFigurineImageSrc(editingFigure)} alt={editingFigure.name} className="h-full w-full object-contain" />
              </div>
              <p className="nfig-kicker">{editingFigure.series}</p>
              <h3 className="mt-1 text-2xl font-black uppercase">Edit Figure</h3>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSaveEdit();
              }}
            >
              <label className="mb-4 block font-semibold">
                <span className="mb-2 block text-xs uppercase tracking-widest text-slate-300">Name</span>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="nfig-input"
                />
              </label>

              <label className="mb-4 block font-semibold">
                <span className="mb-2 block text-xs uppercase tracking-widest text-slate-300">Price</span>
                <input
                  type="number"
                  min="0.01"
                  step="any"
                  value={editPrice}
                  onChange={(e) => setEditPrice(e.target.value)}
                  className="nfig-input"
                />
              </label>

              <label className="mb-6 block font-semibold">
                <span className="mb-2 block text-xs uppercase tracking-widest text-slate-300">Rating</span>
                <input
                  type="number"
                  min="3"
                  max="7"
                  step="1"
                  value={editRating}
                  onChange={(e) => setEditRating(e.target.value)}
                  className="nfig-input"
                />
              </label>

              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
                <button
                  type="button"
                  onClick={() => closeEditModal()}
                  className="nfig-button-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="nfig-button-primary"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>

            {editingFigure.stock === 0 && (
              <div className="mt-5 border-t border-red-400/20 pt-5">
                <button
                  type="button"
                  onClick={handleDeleteFigure}
                  disabled={isDeleting}
                  className="nfig-button-danger w-full"
                >
                  {isDeleting ? 'Deleting...' : 'Delete Figure'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
